import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { ApiError } from '@/util/errors/api-error'
import { deleteMatterMember } from '@/matter/fetch/delete-matter-member'
import { postAddMatterMembers } from '@/matter/fetch/post-add-matter-members'
import { postTransferMatterOwnership } from '@/matter/fetch/post-transfer-matter-ownership'
import { patchMatterMemberRole } from '@/matter/fetch/patch-matter-member-role'
import { MattersActions } from '@/matter/store/slice'
import { selectMatterMembers, selectMatterMembersError, selectMatterMembersInitialLoadComplete } from '@/matter/store/selectors'
import { getTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import {
  getCollaborationOrganizationId,
  trackMatterInviteSendFailed,
  trackMatterInviteSentForRecipients,
} from '@/analytics/utils/matter-collaboration-analytics'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { AuthContext } from '@/context/auth-context'
import { useOrgDirectoryUsers } from '@/matter/hooks/useOrgDirectoryUsers'

import { AddMembersStatus, MemberListStatus, type MatterCollaborator, type OrgUserOption, type RemoveMemberResult, type TransferOwnershipResult } from './types'
import { AddMemberOutcome, type AddMatterMemberResult, type MatterMemberRead, type MatterAssignableRole } from './schemas'
import { kUnavailableMemberEmail, kUnavailableMemberName } from './matter-member-copy'

export interface UseManageMatterMembersResult {
  collaborators: MatterCollaborator[]
  listStatus: MemberListStatus
  orgUsers: OrgUserOption[]
  addMembersStatus: AddMembersStatus
  /** Number of members added on the last fully successful add; drives the success toast text. */
  addedCount: number
  /** Name of the member removed by the last successful removal; drives the success toast text. */
  removedMemberName: string | null
  /** Add-flow failure message, shown above the footer actions. (Removal errors surface in the confirm modal.) */
  actionError: string | null
  addMembers: (userIds: string[], role?: MatterAssignableRole) => Promise<void>
  /** True when the role change succeeds in the current dialog session. */
  changeRole: (userId: string, role: MatterAssignableRole) => Promise<boolean>
  roleChangeUserId: string | null
  roleChangeError: string | null
  removeMember: (userId: string) => Promise<RemoveMemberResult>
  transferOwnership: (userId: string) => Promise<TransferOwnershipResult>
}

function mapMemberReadToCollaborator(member: MatterMemberRead): MatterCollaborator {
  const email = member.email ?? kUnavailableMemberEmail

  return {
    userId: member.user_id,
    displayName: member.display_name ?? member.email ?? kUnavailableMemberName,
    email,
    orgRole: member.org_role,
    matterRole: member.matter_role,
    addedAt: member.accepted_at ?? '',
  }
}

function getAddMembersErrorMessage(results: AddMatterMemberResult[]): string | null {
  const invitedCount = results.filter((result) => result.outcome === AddMemberOutcome.INVITED).length
  const alreadyInvitedCount = results.filter((result) => result.outcome === AddMemberOutcome.ALREADY_INVITED).length
  const alreadyMemberCount = results.filter((result) => result.outcome === AddMemberOutcome.ALREADY_MEMBER).length
  const notFoundCount = results.filter((result) => result.outcome === AddMemberOutcome.NOT_FOUND).length

  if (invitedCount === results.length) {
    return null
  }

  if (alreadyMemberCount === results.length) {
    return results.length === 1 ? 'That member is already on this matter.' : 'Those members are already on this matter.'
  }

  if (alreadyInvitedCount === results.length) {
    return results.length === 1 ? 'That member has already been invited to this matter.' : 'Those members have already been invited to this matter.'
  }

  const details: string[] = []

  if (alreadyMemberCount > 0) {
    details.push(alreadyMemberCount === 1 ? '1 member is already on this matter.' : `${alreadyMemberCount} members are already on this matter.`)
  }

  if (alreadyInvitedCount > 0) {
    details.push(alreadyInvitedCount === 1 ? '1 member has already been invited.' : `${alreadyInvitedCount} members have already been invited.`)
  }

  if (notFoundCount > 0) {
    details.push(notFoundCount === 1 ? '1 member could not be found.' : `${notFoundCount} members could not be found.`)
  }

  if (invitedCount > 0) {
    return `Some selected members were not added. ${details.join(' ')}`
  }

  return details.join(' ') || 'Could not add members. Try again.'
}

/**
 * Controls the Manage Members modal: the member list plus add, remove, and ownership actions
 * and the org directory to pick from.
 *
 * `entrySource` attributes the surface that opened the modal onto `MatterInviteSent` /
 * `MatterInviteSendFailed` (see analytics/README.md "Matter Collaboration").
 */
export function useManageMatterMembers(
  matterId: string,
  enabled: boolean,
  entrySource: MatterCollaborationEntrySource = MatterCollaborationEntrySource.Unknown
): UseManageMatterMembersResult {
  const dispatch = useAppDispatch()
  const { userAccountData } = useContext(AuthContext)
  const organizationId = getCollaborationOrganizationId(userAccountData)

  // Member list lives in Redux; all consumers stay in sync automatically.
  const rawMembers = useAppSelector((state) => selectMatterMembers(state, enabled ? matterId : undefined))
  const membersError = useAppSelector((state) => selectMatterMembersError(state, enabled ? matterId : undefined))
  const membersInitialLoadComplete = useAppSelector((state) => selectMatterMembersInitialLoadComplete(state, enabled ? matterId : undefined))

  const collaborators = useMemo(() => rawMembers.map(mapMemberReadToCollaborator), [rawMembers])

  // A background poll failure shouldn't hide an already-loaded list — only show the error state
  // when we have no cached members to fall back on.
  const listStatus = membersInitialLoadComplete ? MemberListStatus.SUCCESS : membersError ? MemberListStatus.ERROR : MemberListStatus.LOADING

  // Ephemeral UI state — scoped to this modal session, not shared.
  const [addMembersStatus, setAddMembersStatus] = useState<AddMembersStatus>(AddMembersStatus.IDLE)
  const [addedCount, setAddedCount] = useState(0)
  const [removedMemberName, setRemovedMemberName] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [roleChangeUserId, setRoleChangeUserId] = useState<string | null>(null)
  const [roleChangeError, setRoleChangeError] = useState<string | null>(null)
  const roleActionContext = useRef(0)

  useEffect(() => {
    roleActionContext.current += 1
    setRoleChangeUserId(null)
    setRoleChangeError(null)
  }, [matterId, enabled])

  const { orgUsers } = useOrgDirectoryUsers(enabled && !!matterId)

  useEffect(() => {
    if (!enabled || !matterId) {
      setAddMembersStatus(AddMembersStatus.IDLE)
      setAddedCount(0)
      setRemovedMemberName(null)
      setActionError(null)
      return
    }

    dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: false }))
  }, [enabled, matterId, dispatch])

  const addMembers = useCallback(
    async (userIds: string[], role: MatterAssignableRole = 'collaborator') => {
      if (!enabled || !matterId || userIds.length === 0) return

      setActionError(null)
      setAddMembersStatus(AddMembersStatus.LOADING)

      try {
        const response = await postAddMatterMembers(matterId, userIds, role)
        const errorMessage = getAddMembersErrorMessage(response.results)
        const invitedRecipientUserIds = response.results.filter((result) => result.outcome === AddMemberOutcome.INVITED).map((result) => result.user_id)

        trackMatterInviteSentForRecipients({
          track: getTrackEventGlobal(),
          matterId,
          organizationId,
          selectedRecipientCount: userIds.length,
          invitedRecipientUserIds,
          matterInviteEntrySource: entrySource,
        })

        if (errorMessage) {
          setActionError(errorMessage)
          setAddMembersStatus(AddMembersStatus.ERROR)
        } else {
          setAddedCount(response.results.length)
          setAddMembersStatus(AddMembersStatus.SUCCESS)
        }
      } catch (error) {
        trackMatterInviteSendFailed({
          track: getTrackEventGlobal(),
          matterId,
          organizationId,
          selectedRecipientCount: userIds.length,
          matterInviteEntrySource: entrySource,
          error,
        })
        setAddMembersStatus(AddMembersStatus.ERROR)
        setActionError('Could not add members. Try again.')
      } finally {
        // Refetch after the change: detail for this matter's members, list for the matters table. The list
        // refresh is forced so it supersedes a stale list poll that would otherwise revert member_count.
        dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: true }))
        dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
      }
    },
    [enabled, matterId, organizationId, entrySource, dispatch]
  )

  const changeRole = useCallback(
    async (userId: string, role: MatterAssignableRole) => {
      if (!enabled || !matterId) return false
      const context = roleActionContext.current
      setRoleChangeUserId(userId)
      setRoleChangeError(null)
      try {
        await patchMatterMemberRole(matterId, userId, role)
        return roleActionContext.current === context
      } catch (error) {
        if (roleActionContext.current === context) {
          setRoleChangeError(
            error instanceof ApiError && error.status === 403 ? 'Only the matter owner can change member roles.' : 'Could not change member role. Try again.'
          )
        }
        return false
      } finally {
        if (roleActionContext.current === context) setRoleChangeUserId(null)
        dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: true }))
        dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
      }
    },
    [enabled, matterId, dispatch]
  )

  const removeMember = useCallback(
    async (userId: string): Promise<RemoveMemberResult> => {
      if (!enabled || !matterId) return 'Could not remove member. Try again.'

      // Capture the name before the list refresh drops the member.
      const removedName = collaborators.find((c) => c.userId === userId)?.displayName ?? null

      // Reset first so the container's toast effect re-fires for every successful removal.
      setRemovedMemberName(null)

      let removeError: RemoveMemberResult = null
      try {
        await deleteMatterMember(matterId, userId)
        setRemovedMemberName(removedName)
      } catch (error) {
        removeError = error instanceof ApiError && error.status === 403 ? 'Only the matter owner can remove members.' : 'Could not remove member. Try again.'
      } finally {
        // Refetch after the change: detail for this matter's members, list for the matters table. The list
        // refresh is forced so it supersedes a stale list poll that would otherwise revert member_count.
        dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: true }))
        dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
      }
      return removeError
    },
    [enabled, matterId, dispatch, collaborators]
  )

  const transferOwnership = useCallback(
    async (userId: string): Promise<TransferOwnershipResult> => {
      if (!enabled || !matterId) return { ok: false, message: 'Could not transfer ownership. Try again.' }
      try {
        const response = await postTransferMatterOwnership(matterId, userId)
        dispatch(MattersActions.upsertMatter(response.matter))
        return { ok: true, response }
      } catch (error) {
        const message =
          error instanceof ApiError && error.status === 403
            ? 'Only the matter owner can transfer ownership of a standard matter.'
            : 'Could not transfer ownership. Try again.'
        return { ok: false, message }
      } finally {
        dispatch(MattersActions.refreshMatterDetail({ matterId, forceRefresh: true }))
        dispatch(MattersActions.refreshMatterList({ forceRefresh: true }))
      }
    },
    [enabled, matterId, dispatch]
  )

  return {
    collaborators,
    listStatus,
    orgUsers,
    addMembersStatus,
    addedCount,
    removedMemberName,
    actionError,
    addMembers,
    removeMember,
    transferOwnership,
    changeRole,
    roleChangeUserId,
    roleChangeError,
  }
}
